/**
 * HR/Owner — ดูประวัติการเข้างานของพนักงานรายคนทั้งช่วง
 * (มา/ขาด/ลา + เข้า-ออกกี่โมงรายวัน + สาย). เลือกพนักงาน + ช่วงเวลา.
 * ใช้ AttendanceTimeline ร่วมกับหน้า "ของฉัน".
 */
import { useState } from 'react';
import { User } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useEmployees, useEmployeeAttendance } from '../../hooks';
import { EmployeePicker } from '../../components/ui/EmployeePicker';
import { AttendanceDayEditor } from '../../components/domain/AttendanceDayEditor';
import { useMyCapabilities } from '../../hooks';
import { useAuthStore } from '../../lib/store';
import {
  AttendanceTimeline,
  AttendanceRangePresets,
  rangeForPreset,
  thisMonthRange,
  type RangePreset,
} from '../../components/domain/AttendanceTimeline';

interface EmpOpt {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  nickname?: string | null;
  status?: string | null;
  department?: string | null;
  position?: string | null;
}

export function EmployeeAttendance() {
  const { data: employees } = useEmployees() as { data: EmpOpt[] | undefined };
  const [empCode, setEmpCode] = useState<string>('');
  const [range, setRange] = useState(thisMonthRange);
  const [preset, setPreset] = useState<RangePreset>('this-month');
  const { data, isLoading } = useEmployeeAttendance(empCode || null, range);
  // แก้เวลาได้เฉพาะเจ้าของ + HR ที่มีสิทธิ์ "งานประจำวัน" — ตรงกับด่าน
  // requireCapability('daily_ops') ฝั่ง API (หัวหน้างานดูได้อย่างเดียว ไม่โชว์ปุ่ม)
  const role = useAuthStore((st) => st.profile?.role);
  const { data: caps } = useMyCapabilities();
  const canEdit = role === 'OWNER' || (caps?.includes('daily_ops') ?? false);

  function applyPreset(p: RangePreset) {
    setPreset(p);
    setRange(rangeForPreset(p));
  }

  const opts = (employees ?? []).filter((e) => e.status === 'active' || e.status === 'probation');

  return (
    <AdminLayout title="ประวัติเข้างานรายคน">
      <div className="card p-3 mb-3">
        <label className="label">เลือกพนักงาน</label>
        <EmployeePicker employees={opts} value={empCode || null} onChange={(c) => setEmpCode(c ?? '')} />
      </div>

      {!empCode ? (
        <div className="card p-6 text-center text-gray-400">
          <User className="w-10 h-10 mx-auto mb-2 text-muted" />
          <p className="text-sm">เลือกพนักงานเพื่อดูประวัติการเข้างาน</p>
        </div>
      ) : (
        <>
          <AttendanceRangePresets preset={preset} onChange={applyPreset} />
          <AttendanceTimeline
            data={data}
            isLoading={isLoading}
            renderEditor={
              canEdit
                ? (day, close) => <AttendanceDayEditor empCode={empCode} day={day} onDone={close} />
                : undefined
            }
          />
        </>
      )}
    </AdminLayout>
  );
}
